export default function registerNewsletterComponent(Alpine, z) {

    function toClientErrorPath(modelStateKey) {
        if (!modelStateKey) return '';

        // Convert indexers: EmailAddresses[0].EmailAddress -> EmailAddresses.0.EmailAddress
        let key = modelStateKey.replace(/\[(\d+)\]/g, '.$1');

        // Split and camelCase each segment (preserve numeric segments)
        const parts = key.split('.').filter(Boolean);

        const camel = (s) => (s.length ? (s[0].toLowerCase() + s.slice(1)) : s);

        return parts
            .map(p => (/^\d+$/.test(p) ? p : camel(p)))
            .join('.');
    }

    async function readProblemDetails(response) {
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/problem+json')) return null;

        try {
            return await response.json();
        } catch {
            return null;
        }
    }

    function isValidationProblem(problem) {
        // ValidationProblemDetails has an `errors` object in ASP.NET Core
        return !!(problem && typeof problem === 'object' && problem.errors && typeof problem.errors === 'object');
    }

    async function classifyProblemResponse(response) {
        const problem = await readProblemDetails(response);

        return {
            ok: response.ok,
            status: response.status,
            contentType: response.headers.get('content-type') || '',
            problem,
            kind: !problem ? 'not-problem-json'
                : isValidationProblem(problem) ? 'validation-problem'
                    : 'problem'
        };
    }

    const NewsletterSubscribeSchema = z.object({        
        emailAddress: z.email({ pattern: /^(\w+([-+.]\w+)*@\w+([-.]\.w+)*\.\w+([-.]\.w+)*)$/, error: "Ongeldig e-mailadres." }).min(1, "E-mailadres is verplicht").max(100, "E-mailadres mag maximaal 100 tekens bevatten"),
    });

    Alpine.data('newsletter', () => ({
        emailAddress: '',
        _saving: false,
        _successMessage: '',
        _errorMessage: '',

        // Field-level errors keyed by zod issue path (e.g. "correspondenceAddress.addressLine")
        errors: {},

        clearErrors() {
            this.errors = {};
        },

        setZodErrors(zodError) {
            const next = {};

            for (const issue of zodError.issues ?? []) {
                const path = issue.path.join('.');
                if (!path) continue;

                // keep first message per field (or change to array if you want multiple)
                if (!next[path]) next[path] = issue.message;
            }

            this.errors = next;
        },

        errorFor(path) {
            return this.errors?.[path] ?? '';
        },

        hasError(path) {
            return !!this.errorFor(path);
        },

        clearError(path) {
            if (this.errors?.[path]) {
                const { [path]: _, ...rest } = this.errors;
                this.errors = rest;
            }
        },

        setServerValidationErrors(problemDetails) {
            const next = {};

            const errors = problemDetails?.errors;
            if (errors && typeof errors === 'object') {
                for (const [key, messages] of Object.entries(errors)) {
                    const msg = Array.isArray(messages) ? (messages[0] ?? '') : String(messages ?? '');
                    if (!msg) continue;

                    const path = toClientErrorPath(key);
                    if (!path) continue;

                    if (!next[path]) next[path] = msg;
                }
            }

            this.errors = next;
        },

        async submit() {
            this._saving = true;
            this._successMessage = '';
            this._errorMessage = '';
            this.clearErrors();
            const result = NewsletterSubscribeSchema.safeParse({ emailAddress: this.emailAddress });

            if (!result.success) {
                this.setZodErrors(result.error);
                this._saving = false;
                return;
            } 

            var data = result.data;
            try {
                const response = await fetch('/api/newsletter', {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Accept-Language': ___culture
                     },
                    body: JSON.stringify(data),
                });

                if (!response.ok) { 
                    const info = await classifyProblemResponse(response);

                    if (info.kind === 'validation-problem') {
                        this.setServerValidationErrors(info.problem);
                        this._saving = false;
                        return;
                    }

                    if (info.kind === 'problem') {
                        // ProblemDetails (non-validation): use title/detail for UI/logging
                        console.warn('Server problem:', info.problem);
                        this._errorMessage = info.problem?.detail || info.problem?.title || 'Er is een fout opgetreden.';
                        this._saving = false;
                        return;
                    }

                    throw new Error(`Request failed (${info.status})`);
                }
                else {
                    this._successMessage = 'Je hebt je succesvol aangemeld!';
                    this.emailAddress = '';
                }
                
            } catch (error) {
                this._errorMessage = 'Er is een fout opgetreden bij het aanmelden. Probeer het opnieuw.';
            }

            this._saving = false;
        }
    }));
}